import { useId, useRef, useState } from 'react';
import { Camera, FileUp, Paperclip, X } from 'lucide-react';
import { formatFileSize } from '@blimo/shared';
import { Button } from './button.jsx';
import { cn } from '../lib/utils.js';

/**
 * File picker with drag & drop (and optional camera input on mobile). Controlled: `files` + `onFilesChange`.
 * Size/type limits are only hints – the API validates.
 * @param {{
 *   files: File[],
 *   onFilesChange: (files: File[]) => void,
 *   label?: string,
 *   hint?: import('react').ReactNode,
 *   accept?: string,
 *   multiple?: boolean,
 *   maxFiles?: number,
 *   camera?: boolean,
 *   disabled?: boolean,
 *   className?: string,
 * }} props
 */
export function FileDropzone({
  files,
  onFilesChange,
  label = 'Dateien',
  hint,
  accept,
  multiple = true,
  maxFiles,
  camera = false,
  disabled = false,
  className,
}) {
  const id = useId();
  const inputRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const cameraRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const [dragging, setDragging] = useState(false);

  /** @param {FileList|File[]|null|undefined} list */
  function add(list) {
    const incoming = Array.from(list ?? []);
    if (incoming.length === 0) return;
    let next = multiple ? [...files, ...incoming] : incoming.slice(0, 1);
    if (maxFiles) next = next.slice(0, maxFiles);
    onFilesChange(next);
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        data-slot="file-dropzone"
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-5 text-center text-sm transition-colors',
          dragging ? 'border-primary bg-accent' : 'bg-card',
          disabled && 'opacity-50',
        )}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) add(e.dataTransfer?.files);
        }}
      >
        <FileUp className="size-5 text-muted-foreground" aria-hidden="true" />
        <p className="text-muted-foreground">Dateien hierher ziehen oder</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => inputRef.current?.click()}>
            <Paperclip aria-hidden="true" /> {multiple ? 'Dateien auswählen' : 'Datei auswählen'}
          </Button>
          {camera && (
            <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => cameraRef.current?.click()}>
              <Camera aria-hidden="true" /> Foto aufnehmen
            </Button>
          )}
        </div>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        <input
          ref={inputRef}
          id={id}
          type="file"
          className="sr-only"
          aria-label={label}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
        {camera && (
          <input
            ref={cameraRef}
            type="file"
            className="sr-only"
            aria-label="Foto aufnehmen"
            accept={accept ?? 'image/*,application/pdf'}
            capture="environment"
            disabled={disabled}
            tabIndex={-1}
            onChange={(e) => {
              add(e.target.files);
              e.target.value = '';
            }}
          />
        )}
      </div>
      {files.length > 0 && (
        <ul className="flex flex-col gap-1" aria-label={`Ausgewählte ${label}`}>
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
              <Paperclip className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <span className="numeric text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`${file.name} entfernen`}
                disabled={disabled}
                onClick={() => onFilesChange(files.filter((_, i) => i !== index))}
              >
                <X aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
